import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useRef } from 'react';
import { useFieldArray, useForm, type SubmitHandler } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import {
  type TelInput,
  TelephoneInput,
} from '@/pages/Schedule/components/TelephoneInput';
import { OrganizationRole } from '@/types/gql.generated';
import {
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  IconButton,
  Input,
  Link,
  Text,
} from '@/ui';
import {
  getGQLErrorMessage,
  isGQLError,
  isGQLErrorOfType,
} from '@/utils/errors';
import {
  FamilyAddMembersSchema,
  type FamilyAddMembersDto,
} from '../dto/familyAddMembers.dto';
import { useInviteOrganizationMembers } from '../hooks/useInviteOrganizationMembers';
import type { OrgMember } from '../hooks/useOrganizationMembers';
import { getSeatUsage } from '../lib/seatLimit';

type Props = {
  organizationId: string;
  members: OrgMember[];
  /** Lowercased emails that already have a pending invite, expired included. */
  pendingInviteEmails: string[];
  occupiedSeats: number;
  seatLimit: number | null | undefined;
  onClose: () => void;
};

const emptyInvitee = { name: '', email: '', phoneNumber: '' };

/**
 * The family workspace's invite form: name, email and phone per person, rather
 * than the business form's single list of email addresses.
 *
 * Taking the number here is what stops a new member landing in the state behind
 * AH-2426 — able to write a text reminder with nobody set up to receive it, and
 * unable to fix that themselves because contact details are owner-only.
 */
export const FamilyAddMembersSection = ({
  organizationId,
  members,
  pendingInviteEmails,
  occupiedSeats,
  seatLimit,
  onClose,
}: Props) => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'organizations',
  });
  const { inviteMembers, isPending } =
    useInviteOrganizationMembers(organizationId);
  const toast = useToast();
  const isMobileBreakpoint = useIsMobileBreakpoint();

  const {
    control,
    register,
    handleSubmit: onSubmit,
    setError: setFieldError,
    clearErrors,
    formState: { errors },
  } = useForm<FamilyAddMembersDto>({
    defaultValues: { invitees: [emptyInvitee], authorized: false },
    resolver: standardSchemaResolver(FamilyAddMembersSchema),
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'invitees',
  });

  // intl-tel-input reports validity and the E.164 value imperatively, so each
  // row needs its own instance handle. Keyed by the field-array id rather than
  // the index, which shifts when a row is removed.
  const itiRefs = useRef(new Map<string, { current: TelInput | null }>());
  const getItiRef = (fieldId: string) => {
    const existing = itiRefs.current.get(fieldId);
    if (existing) {
      return existing;
    }
    const created = { current: null as TelInput | null };
    itiRefs.current.set(fieldId, created);
    return created;
  };

  const { remaining, hasReachedLimit, isUnlimited } = getSeatUsage(
    seatLimit,
    occupiedSeats
  );

  const handleSubmit: SubmitHandler<FamilyAddMembersDto> = (values) => {
    // Ignore repeat submits while an invite request is already in flight.
    if (hasReachedLimit || isPending) {
      return;
    }

    // Read every row before bailing, so someone who mistyped two numbers sees
    // both flagged rather than fixing them one submit at a time.
    const phoneNumbers = fields.map(
      (field) => getItiRef(field.id).current?.getNumber()?.trim() ?? ''
    );
    let hasInvalidPhone = false;
    fields.forEach((field, index) => {
      const iti = getItiRef(field.id).current;
      if (!phoneNumbers[index] || !iti?.isValidNumber()) {
        setFieldError(`invitees.${index}.phoneNumber`, {
          message: t('invite_family.phone.invalid'),
        });
        hasInvalidPhone = true;
      }
    });
    if (hasInvalidPhone) {
      return;
    }

    const existingEmails = new Set([
      ...members
        .map((member) => member.user.email?.toLowerCase())
        .filter((email): email is string => !!email),
      ...pendingInviteEmails,
    ]);
    const seen = new Set<string>();
    const newInvitees = values.invitees
      .map((invitee, index) => ({
        email: invitee.email.trim(),
        name: invitee.name.trim(),
        phoneNumber: phoneNumbers[index],
        role: OrganizationRole.Editor,
      }))
      .filter(({ email }) => {
        const lower = email.toLowerCase();
        if (existingEmails.has(lower) || seen.has(lower)) {
          return false;
        }
        seen.add(lower);
        return true;
      });

    if (newInvitees.length === 0) {
      setFieldError('invitees.0.email', { message: t('invite.all_existing') });
      return;
    }

    const membersInput = isUnlimited
      ? newInvitees
      : newInvitees.slice(0, remaining);

    inviteMembers(
      { members: membersInput },
      {
        onError: (err) => {
          setFieldError('root', {
            message: isGQLErrorOfType(err, 'OrganizationSeatLimitReached')
              ? t('invite.limit_reached')
              : isGQLError(err)
                ? (getGQLErrorMessage(err) ?? t('invite.error'))
                : t('invite.error'),
          });
        },
        onSuccess: () => {
          const skippedCount = newInvitees.length - membersInput.length;
          toast.notify(
            membersInput.length === 1
              ? t('invite.success', { count: 1, email: membersInput[0].email })
              : t('invite.success', { count: membersInput.length })
          );
          // Some rows may have been dropped to fit the remaining seats — say so
          // rather than silently truncating, and stay open so the message has
          // somewhere to live.
          if (skippedCount > 0) {
            setFieldError('root', {
              message: t('invite.skipped', { count: skippedCount }),
            });
            return;
          }
          onClose();
        },
      }
    );
  };

  const canAddRow = isUnlimited || fields.length < remaining;

  return (
    <Flex
      as="form"
      direction="column"
      gap="4"
      onSubmit={onSubmit(handleSubmit)}
    >
      {!!errors.root && (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>{errors.root.message}</AlertTitle>
        </Alert>
      )}

      {fields.map((field, index) => (
        <Flex align="flex-start" gap="2" key={field.id}>
          <Flex direction={{ base: 'column', md: 'row' }} flex="1" gap="2">
            <FormControl isInvalid={!!errors.invitees?.[index]?.name}>
              <FormLabel>{t('invite_family.name.label')}</FormLabel>
              <Input
                autoFocus={index === 0 && !isMobileBreakpoint}
                type="text"
                {...register(`invitees.${index}.name`)}
              />
              <FormErrorMessage>
                {errors.invitees?.[index]?.name?.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.invitees?.[index]?.email}>
              <FormLabel>{t('invite_family.email.label')}</FormLabel>
              <Input type="email" {...register(`invitees.${index}.email`)} />
              <FormErrorMessage>
                {errors.invitees?.[index]?.email?.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl
              isInvalid={!!errors.invitees?.[index]?.phoneNumber}
              sx={{ '.iti': { width: '100%' } }}
            >
              <FormLabel>{t('invite_family.phone.label')}</FormLabel>
              <TelephoneInput
                itiInstanceRef={getItiRef(field.id)}
                {...register(`invitees.${index}.phoneNumber`)}
              />
              <FormErrorMessage>
                {errors.invitees?.[index]?.phoneNumber?.message}
              </FormErrorMessage>
            </FormControl>
          </Flex>

          {/* The first row is the form itself, so it has nothing to remove. */}
          {fields.length > 1 && (
            <IconButton
              alignSelf="center"
              aria-label={t('invite_family.remove')}
              icon={<Icon icon="X" />}
              size="sm"
              variant="ghost"
              onClick={() => {
                itiRefs.current.delete(field.id);
                remove(index);
              }}
            />
          )}
        </Flex>
      ))}

      {canAddRow && (
        <Box>
          <Button
            size="sm"
            variant="link"
            onClick={() => {
              clearErrors('root');
              append(emptyInvitee);
            }}
          >
            {t('invite_family.add_another')}
          </Button>
        </Box>
      )}

      <FormControl isInvalid={!!errors.authorized}>
        <FormLabel alignItems="center" display="flex" gap="2">
          <Checkbox {...register('authorized')} />
          {t('invite_family.authorized.label')}
        </FormLabel>
        <FormErrorMessage>{errors.authorized?.message}</FormErrorMessage>
      </FormControl>

      <Text color="customgray.mid" fontSize="small">
        <Trans i18nKey="invite_family.privacy" t={t}>
          When you add a text message recipient, we&apos;ll send each person a
          text welcoming them to Agenda Hero once they join. After that, only
          you control who gets a text and when. Read our{' '}
          <Link colorScheme="dark" href="/privacy" target="_blank">
            privacy policy
          </Link>
          .
        </Trans>
      </Text>

      <Flex align="center" gap="3" justify="flex-end">
        <Button isDisabled={isPending} variant="link" onClick={onClose}>
          {t('invite.cancel')}
        </Button>
        <Button isLoading={isPending} type="submit">
          {t('invite.submit')}
        </Button>
      </Flex>
    </Flex>
  );
};
